const GENERIC_ERROR = "Something went wrong, please try again.";

/**
 * Areakart catalog ids are BIGSERIAL, so a valid one is always a positive integer.
 *
 * `Number.isFinite` is not a sufficient guard: Number(null), Number("") and Number([]) all yield 0,
 * which passes it and then reaches the API as id 0. The backend rejects the entire request
 * ("Unknown or inactive Areakart add-on id(s): [0]"), so a single missing id anywhere in the
 * selection made the whole plan impossible to save.
 */
export function toCatalogId(value) {
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : null;
}

function normalizeEmpty(value) {
  if (value === "" || value === null || value === undefined) return null;
  return value;
}

function safeNumber(value, fallback = null) {
  const normalized = normalizeEmpty(value);
  if (normalized === null) return fallback;
  const num = Number(normalized);
  return Number.isFinite(num) ? num : fallback;
}

function normalizeString(value, fallback = null) {
  const normalized = normalizeEmpty(value);
  if (normalized === null) return fallback;
  const trimmed = String(normalized).trim();
  return trimmed ? trimmed : fallback;
}

function normalizeFeature(feature, index) {
  if (!feature || typeof feature !== "object") return null;

  const type = normalizeString(feature.type, "TEXT");
  const valueUnit = normalizeString(feature.valueUnit, "NONE");
  const options = Array.isArray(feature.options)
    ? feature.options
        .map((option) => normalizeString(option))
        .filter(Boolean)
    : null;

  let value = normalizeEmpty(feature.value);
  if (type === "BOOLEAN") value = Boolean(feature.value);
  if (type === "NUMBER") value = safeNumber(feature.value);
  if (type === "RANGE") value = normalizeEmpty(feature.value);

  return {
    name: normalizeString(feature.name, `feature_${index}`),
    label: normalizeString(feature.label, normalizeString(feature.name, "")),
    value,
    type,
    editable: Boolean(feature.editable),
    minValue: safeNumber(feature.minValue),
    maxValue: safeNumber(feature.maxValue),
    valueUnit,
    unitLabel: normalizeString(feature.unitLabel),
    options: options && options.length ? options : null,
    sortOrder: safeNumber(feature.sortOrder, index) ?? index,
  };
}

function buildTierDiscount(basePricePerMonth, perMonthPrice) {
  const base = safeNumber(basePricePerMonth);
  const tier = safeNumber(perMonthPrice);
  if (base === null || tier === null || base <= 0 || tier >= base) return null;
  const discount = ((base - tier) / base) * 100;
  return Number(discount.toFixed(2));
}

function normalizePricingTier(tier, index, basePricePerMonth, { isUpdate = false } = {}) {
  if (!tier || typeof tier !== "object") return null;

  const billingMonths = safeNumber(tier.billingMonths);
  const perMonthPrice = safeNumber(tier.perMonthPrice, 0);
  const computedTotal = billingMonths !== null ? perMonthPrice * billingMonths : 0;
  const totalAmount = safeNumber(tier.totalAmount, computedTotal);
  const discountPercent =
    safeNumber(tier.discountPercent) ?? buildTierDiscount(basePricePerMonth, perMonthPrice);

  const normalized = {
    billingMonths,
    perMonthPrice,
    totalAmount,
  };

  if (isUpdate && normalizeString(tier.id)) {
    normalized.id = normalizeString(tier.id);
  }
  if (discountPercent !== null) {
    normalized.discountPercent = discountPercent;
  }
  if (normalizeString(tier.razorpayPlanId)) {
    normalized.razorpayPlanId = normalizeString(tier.razorpayPlanId);
  }

  if (normalized.billingMonths === null) {
    return null;
  }

  normalized.sortOrder = index;
  return normalized;
}

export function normalizeAdminPlanPayload(payload, { isUpdate = false } = {}) {
  const basePricePerMonth = safeNumber(payload?.basePricePerMonth, 0) ?? 0;
  const pricingTiers = Array.isArray(payload?.pricingTiers)
    ? payload.pricingTiers
        .map((tier, index) =>
          normalizePricingTier(tier, index, basePricePerMonth, { isUpdate })
        )
        .filter(Boolean)
        .sort((a, b) => a.billingMonths - b.billingMonths)
        .map(({ sortOrder, ...tier }) => tier)
    : [];

  // Last line of defence before the request leaves the browser. Number(null) is 0, which passes
  // Number.isFinite but is never a real catalog id, and the API rejects the entire save because of it.
  const includedServiceIds = Array.isArray(payload?.includedServiceIds)
    ? payload.includedServiceIds.map(toCatalogId).filter((n) => n !== null)
    : [];

  const availableAddOnIds = Array.isArray(payload?.availableAddOnIds)
    ? payload.availableAddOnIds.map(toCatalogId).filter((n) => n !== null)
    : [];

  const features = Array.isArray(payload?.features)
    ? payload.features.map(normalizeFeature).filter(Boolean)
    : [];

  return {
    ...(isUpdate && normalizeString(payload?.id) ? { id: normalizeString(payload.id) } : {}),
    name: normalizeString(payload?.name, ""),
    tagline: normalizeString(payload?.tagline),
    description: normalizeString(payload?.description),
    basePricePerMonth,
    currency: normalizeString(payload?.currency, "INR"),
    country: normalizeString(payload?.country, "IN"),
    isPopular: Boolean(payload?.isPopular),
    badgeText: normalizeString(payload?.badgeText),
    taxInclusive: Boolean(payload?.taxInclusive),
    taxPercent: safeNumber(payload?.taxPercent, 0) ?? 0,
    includedServiceIds,
    availableAddOnIds,
    ...(features.length ? { features } : {}),
    pricingTiers,
    sortOrder: safeNumber(payload?.sortOrder, 0) ?? 0,
    active: payload?.active !== false,
  };
}

export function extractSubscriptionApiError(error, fallbackMessage = GENERIC_ERROR) {
  const status = error?.response?.status;
  const data = error?.response?.data;

  const validationMessage = Array.isArray(data?.errors)
    ? data.errors
        .map((item) => {
          if (typeof item === "string") return item;
          if (item?.defaultMessage) return item.defaultMessage;
          if (item?.message) return item.message;
          if (item?.field && item?.error) return `${item.field}: ${item.error}`;
          return null;
        })
        .filter(Boolean)
        .join(", ")
    : null;

  const message =
    validationMessage ||
    data?.message ||
    data?.error ||
    data?.data?.message ||
    error?.message;

  if (status >= 400 && status < 500 && message) return message;
  if (status >= 500) return GENERIC_ERROR;
  return message || fallbackMessage;
}
